import api from './api';
import type { Store, StoreTipo, Product, ProductCategory, Order } from '../types';

class EcommerceService {
  /**
   * Get store types
   */
  async getStoreTypes(): Promise<StoreTipo[]> {
    const response = await api.get<{
      success: boolean;
      data?: StoreTipo[];
      error?: string;
    }>('/public/lojas/tipos');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar tipos de loja');
  }

  /**
   * Get all stores
   */
  async getStores(): Promise<Store[]> {
    const response = await api.get<{
      success: boolean;
      data?: Store[];
      error?: string;
    }>('/public/lojas');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar lojas');
  }

  /**
   * Get stores by type
   */
  async getStoresByType(typeId: string): Promise<Store[]> {
    const response = await api.get<{
      success: boolean;
      data?: Store[];
      error?: string;
    }>(`/public/lojas?tipo=${typeId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar lojas do tipo');
  }

  /**
   * Get store details
   */
  async getStoreDetails(storeId: string): Promise<Store> {
    const response = await api.get<{
      success: boolean;
      data?: Store;
      error?: string;
    }>(`/public/lojas/${storeId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes da loja');
  }

  /**
   * Get store product categories
   */
  async getStoreCategories(storeId: string): Promise<ProductCategory[]> {
    const response = await api.get<{
      success: boolean;
      data?: ProductCategory[];
      error?: string;
    }>(`/public/lojas/${storeId}/categorias`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar categorias da loja');
  }

  /**
   * Get store products
   */
  async getStoreProducts(storeId: string): Promise<Product[]> {
    const response = await api.get<{
      success: boolean;
      data?: Product[];
      error?: string;
    }>(`/public/lojas/${storeId}/produtos`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar produtos da loja');
  }

  /**
   * Get store products by category
   */
  async getStoreProductsByCategory(
    storeId: string,
    categoryId: string
  ): Promise<Product[]> {
    const response = await api.get<{
      success: boolean;
      data?: Product[];
      error?: string;
    }>(`/public/lojas/${storeId}/produtos?categoria=${categoryId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar produtos da categoria');
  }

  /**
   * Get product detail
   */
  async getProductDetail(storeId: string, productId: string): Promise<any> {
    const response = await api.get<{
      success: boolean;
      data?: any;
      error?: string;
    }>(`/public/lojas/${storeId}/produtos/${productId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar produto');
  }

  /**
   * Get customer orders
   */
  async getOrders(): Promise<Order[]> {
    const response = await api.get<{
      success: boolean;
      data?: Order[];
      error?: string;
    }>('/client/orders');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar encomendas');
  }

  /**
   * Get order details
   */
  async getOrderDetails(orderId: string): Promise<Order> {
    const response = await api.get<{
      success: boolean;
      data?: Order;
      error?: string;
    }>(`/client/orders/${orderId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes da encomenda');
  }
}

export default new EcommerceService();
